import React, { useContext, useEffect, useState } from 'react';
import { DragDropContext } from 'react-beautiful-dnd';
import { Layout, Tabs, Button, DatePicker, Spin, App as AntdApp } from 'antd';
import dayjs from 'dayjs';
import HousekeeperColumn from './HousekeeperColumn';
import UnassignedTasksPool from './UnassignedTasksPool';
import AddTaskModal from './AddTaskModal';
import {
    fetchTasks,
    fetchPlandayShifts,
    createTask,
    editTasks,
} from '../helpers/googleCloud';
import {
    filterTasksByTab,
    isMainCleaning,
    sortTasks,
    uniqueArrayById,
} from '../helpers/helpers';
import {
    SHIFTS,
    TABS,
    UNASSIGNED_TASKS_ID,
    UNASSIGNED_TASKS_POOL_ID,
} from '../helpers/constants';
import { signOut } from 'firebase/auth';
import { auth } from '../config/firebase';
import { getStorageItem, removeStorageItem } from '../helpers/localStorage';
import { UserDispatchContext } from '../store/user';
import AddEmployeeModal from './AddEmployeeModal';

const { Header, Content } = Layout;

function formatTo12HourTime(deadline) {
    const d = dayjs(deadline);
    return d.minute() === 0
        ? d.format('hA').toLowerCase()
        : d.format('h:mmA').toLowerCase();
}

function generateReport(assignees) {
    const lines = [];

    const sortedAssignees = [...assignees]
        .filter((h) => h.tasks.length)
        .sort((a, b) => {
            const aFirstName = a.name.split(' ')[0].toLowerCase();
            const bFirstName = b.name.split(' ')[0].toLowerCase();
            return aFirstName.localeCompare(bFirstName);
        });

    sortedAssignees.forEach((assignee) => {
        const [firstName] = assignee.name.split(' ');
        lines.push(`@${firstName}:`);

        const latestTasksMap = new Map();

        assignee.tasks.forEach((task) => {
            const nickname = task.hotelApartment;
            if (!nickname) return;

            const isValidDeadline = dayjs(task.deadline).isValid();

            const existing = latestTasksMap.get(nickname);
            if (
                !existing ||
                (isValidDeadline &&
                    (!dayjs(existing.deadline).isValid() ||
                        dayjs(task.deadline).isAfter(dayjs(existing.deadline))))
            ) {
                latestTasksMap.set(nickname, task);
            }
            if (
                task.title.toLowerCase().includes('mid') &&
                dayjs(task.deadline).isValid()
            ) {
                const date = dayjs(task.deadline);
                const updated = date.hour(17).minute(0).second(0);
                task.deadline = updated.format('YYYY-MM-DD HH:mm');
            } else if (isValidDeadline) {
                const date = dayjs(task.deadline);
                const fifteen = date.hour(15).minute(0).second(0);

                if (date.isBefore(fifteen)) {
                    task.deadline = fifteen.format('YYYY-MM-DD HH:mm');
                }
            }
        });

        const sortedTasks = Array.from(latestTasksMap.values())
            .sort((a, b) => a.deadline.localeCompare(b.deadline))
            .filter(
                (task) =>
                    !(
                        task.listing.tags.includes('Dummy') ||
                        task.listing.tags.includes('Parent')
                    )
            );

        sortedTasks.forEach((task) => {
            const nickname = task.hotelApartment;
            const isNotToday =
                dayjs(task.deadline).isValid() &&
                new Date(task.deadline).toISOString().split('T')[0] !==
                    new Date(task.startTime).toISOString().split('T')[0];

            lines.push(
                `${nickname} –  ${
                    !dayjs(task.deadline).isValid()
                        ? 'Must finish before not set'
                        : isNotToday
                          ? 'no arrival today'
                          : formatTo12HourTime(task.deadline)
                }`
            );
        });

        lines.push('');
    });

    return lines.join('\n');
}

// Order matters: "Bi-monthly" must be checked before "Monthly" since the
// word "monthly" also matches inside "Bi-monthly" titles.
const ADDITIONAL_TYPE_PATTERNS = [
    ['Bi-monthly', /\bbi-?monthly\b/i],
    ['Monthly', /\bmonthly\b/i],
    ['Weekly', /\bweekly\b/i],
];

function getAdditionalType(title) {
    if (!title || isMainCleaning(title)) return null;
    const match = ADDITIONAL_TYPE_PATTERNS.find(([, pattern]) =>
        pattern.test(title)
    );
    return match ? match[0] : null;
}

function Dashboard() {
    const { notification } = AntdApp.useApp();
    const dispatch = useContext(UserDispatchContext);

    const [housekeepers, setHousekeepers] = useState(null);
    const [housekeepersByTab, setHousekeepersByTab] = useState([]);

    const [tasks, setTasks] = useState([]);
    const [unassignedTasks, setUnassignedTasks] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [editingTask, setEditingTask] = useState(null);
    const [guestyEmployees, setGuestyEmployees] = useState(null);

    const [modalsVisible, setModalsVisible] = useState({
        isAddTaskModalVisible: false,
        isAddEmployeeModalVisible: false,
    });

    const { isAddTaskModalVisible, isAddEmployeeModalVisible } = modalsVisible;

    const [date, setDate] = useState(dayjs());
    const [selectedTab, setSelectedTab] = useState('City center');
    const [isDragging, setIsDragging] = useState(false);

    const [editedTasks, setEditingTasks] = useState([]);

    const fetchDataAndSet = async (isRefetch) => {
        setIsLoading(true);

        try {
            const promises = [fetchTasks(date.format('YYYY-MM-DD') + 'T')];

            if (!housekeepers || isRefetch) {
                promises.push(fetchPlandayShifts(date.format('YYYY-MM-DD')));
            }

            const [tasksData, shiftsData] = await Promise.all(promises);

            const tasks = tasksData.map((task) => {
                const deadline = dayjs(task.mustFinishBefore);
                const startTime = dayjs(task.startTime);
                const duration = task.plannedDuration * 60;

                return {
                    ...task,
                    hotelApartment:
                        task.reservation?.listing?.nickname ||
                        task.listing?.nickname ||
                        '',
                    deadline: deadline.format('YYYY-MM-DD HH:mm'),
                    start: startTime.format('YYYY-MM-DD HH:mm'),
                    duration: String(Math.round(duration)),
                    id: task._id,
                };
            });

            organizeTasksByAssignee(tasks, shiftsData);
        } catch (error) {
            console.error('Failed to fetch data:', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchDataAndSet(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [date]);

    useEffect(() => {
        if (housekeepers) {
            setGuestyEmployees(housekeepers.guestyEmployees);
            housekeepersByTasks();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTab, housekeepers, tasks]);

    useEffect(() => {
        if (editedTasks.length > 0) {
            setIsDragging(true);
        }
    }, [editedTasks]);

    const housekeepersByTasks = () => {
        const housekeepersByTab = housekeepers.shifts[SHIFTS[selectedTab]];

        const tasksCopy = JSON.parse(JSON.stringify(tasks));

        let employeeList = [];

        let assignedTasks = filterTasksByTab(tasksCopy, selectedTab).filter(
            (task) => task.assigneeId !== UNASSIGNED_TASKS_ID
        );
        housekeepersByTab.forEach((housekeeper) => {
            employeeList.push({
                name: housekeeper.employee_name,
                employee_name: housekeeper.employee_name,
                id: housekeeper.guestyEmployeeId,
                mail: housekeeper.mail,
                tasks: [],
                tasksCount: 0,
            });
        });

        if (!assignedTasks.length) {
            assignedTasks = tasksCopy;
        }

        const assignedHousekeepers = filterTasksByTab(
            assignedTasks,
            selectedTab
        )
            .filter(
                (task) =>
                    task.assignee && task.assigneeId !== UNASSIGNED_TASKS_ID
            )
            .map((task) => {
                return {
                    employee_name: task.assignee.fullName,
                    mail: task.assignee.email,
                    id: task.assigneeId,
                };
            });

        const newHousekeepers = [];
        for (const housekeeper of uniqueArrayById(assignedHousekeepers)) {
            const housekeeperTasks = tasksCopy.filter(
                (task) =>
                    task.assigneeId === housekeeper.id &&
                    task.listing &&
                    task.listing.nickname !== 'Office Dummy'
            );

            newHousekeepers.push({
                ...housekeeper,
                name: housekeeper.employee_name,
                mail: housekeeper.mail,
                tasks: sortTasks(housekeeperTasks),
                tasksCount: housekeeperTasks.length,
            });
        }

        const unassignedTasks = tasksCopy.filter(
            (task) => task.assigneeId === UNASSIGNED_TASKS_ID
        );

        const normalize = (str) =>
            str.toLowerCase().replace(/\s+/g, ' ').trim();

        const seen = new Set();
        const list = [...newHousekeepers, ...employeeList].filter((item) => {
            const normName = normalize(item.mail);
            if (seen.has(normName)) return false;
            seen.add(normName);
            return true;
        });

        setHousekeepersByTab(list);
        setUnassignedTasks(filterTasksByTab(unassignedTasks, selectedTab));
    };

    const refreshData = () => {
        fetchDataAndSet(true);
    };

    const organizeTasksByAssignee = (tasks, housekeepers) => {
        const newUnassignedTasks = tasks.filter(
            (task) =>
                !task.assignee?.email ||
                !housekeepersByTab.some(
                    (housekeeper) => housekeeper.id === task.assignee?.id
                )
        );

        housekeepers && setHousekeepers(housekeepers);
        setTasks(newUnassignedTasks);
    };

    const onTabChange = (key) => {
        if (isDragging) {
            window.alert(
                'You have unsaved changes. Please save your changes or discard them.'
            );
            return;
        }
        setSelectedTab(key);
        setIsDragging(false);
        setEditingTasks([]);
        fetchDataAndSet();
    };

    const onDateChange = (date) => {
        setDate(date);
    };

    const updateTaskOrderNumbers = (tasks) => {
        let sortIndex = 1;

        if (tasks.length === 0) {
            return [];
        }

        const updatedTasks = [];
        for (let index = 0; index < tasks.length; index++) {
            const task = tasks[index];
            const indexPrefixPattern = /^\d+: /;
            const titleStartsWithIndex = indexPrefixPattern.test(task.title);

            if (
                index > 0 &&
                task.listing.nickname !== tasks[index - 1].listing.nickname
            ) {
                sortIndex++;
            }

            let newTitle;
            if (titleStartsWithIndex) {
                newTitle = task.title.replace(
                    indexPrefixPattern,
                    `${sortIndex}: `
                );
            } else {
                newTitle = `${sortIndex}: ${task.title}`;
            }

            const editedTask = {
                ...task,
                title: newTitle,
            };

            updatedTasks.push(editedTask);
        }

        setEditingTasks((prev) => [
            ...prev.filter(
                (item) => !updatedTasks.some((task) => task._id === item._id)
            ),
            ...updatedTasks,
        ]);

        return updatedTasks;
    };

    const handleAddNewTaskClick = () => {
        setEditingTask(null);
        setModalsVisible((prev) => ({ ...prev, isAddTaskModalVisible: true }));
    };

    const handleAddEmployeeClick = () => {
        setModalsVisible((prev) => ({
            ...prev,
            isAddEmployeeModalVisible: true,
        }));
    };

    const handleEditTask = (task) => {
        setEditingTask(task);
        setModalsVisible((prev) => ({ ...prev, isAddTaskModalVisible: true }));
    };

    const handleFormSubmit = async (values) => {
        const taskData = {
            title: values.title,
            description: values.description,
            startTime: values.startTime,
            duration: values.duration,
            mustFinishBefore: values.deadline,
            assigneeId: editingTask
                ? editingTask.assigneeId
                : values.assigneeId,
            listingId: values.listingId,
        };

        if (editingTask) {
            taskData.id = editingTask.id;
            await editTasks([taskData]);
        } else {
            await createTask(taskData);
        }

        fetchDataAndSet();
        handleTaskModalClose();
    };

    const handleNewEmployeeSubmit = (values) => {
        const splitedString = values.userId.split('_');
        const newEmployee = {
            id: splitedString[0],
            name: splitedString[1],
            tasks: [],
            tasksCount: 0,
        };

        setHousekeepersByTab((prev) => [...prev, newEmployee]);
        handleEmployeeModalClose();
    };

    const handleTaskModalClose = () => {
        setModalsVisible((prev) => ({ ...prev, isAddTaskModalVisible: false }));
        setEditingTask(null);
    };

    const handleEmployeeModalClose = () => {
        setModalsVisible((prev) => ({
            ...prev,
            isAddEmployeeModalVisible: false,
        }));
    };

    const onDragEnd = (result) => {
        const { source, destination } = result;

        if (
            !destination ||
            (source.index === destination.index &&
                source.droppableId === destination.droppableId)
        ) {
            return;
        }

        const sourceId = source.droppableId;
        const destinationId = destination.droppableId;

        let newHousekeepers = [...housekeepersByTab];
        let newUnassignedTasks = [...unassignedTasks];

        const getTaskList = (id) => {
            if (id === UNASSIGNED_TASKS_POOL_ID) {
                return newUnassignedTasks;
            }
            const housekeeper = newHousekeepers.find(
                (h) => `housekeeper-${h.id}` === id
            );
            return housekeeper ? housekeeper.tasks : [];
        };

        const updateHousekeeperTasks = (id, tasks) => {
            newHousekeepers = newHousekeepers.map((h) =>
                `housekeeper-${h.id}` === id
                    ? {
                          ...h,
                          tasks: updateTaskOrderNumbers(tasks),
                          tasksCount: tasks.length,
                      }
                    : h
            );
        };

        let taskList = getTaskList(sourceId);
        const [movedItem] = taskList.splice(source.index, 1);

        const moveTaskToDestination = (
            destinationTaskList,
            movedItem,
            index
        ) => {
            destinationTaskList.splice(index, 0, movedItem);
            movedItem.assigneeId =
                destinationId.split('-')[1] || UNASSIGNED_TASKS_ID;
        };

        if (sourceId === destinationId) {
            // Reordering within the same list
            taskList.splice(destination.index, 0, movedItem);

            if (sourceId === UNASSIGNED_TASKS_POOL_ID) {
                newUnassignedTasks = taskList;
            } else {
                updateHousekeeperTasks(sourceId, taskList);
            }
        } else {
            // Moving between different lists

            const destinationTaskList = getTaskList(destinationId);
            moveTaskToDestination(
                destinationTaskList,
                movedItem,
                destination.index
            );

            const movedTask = destinationTaskList.find(
                (task) => task._id === movedItem._id
            );

            if (isMainCleaning(movedTask.title)) {
                const additionalTasks = taskList.filter(
                    (task) => task.hotelApartment === movedItem.hotelApartment
                );

                if (additionalTasks.length > 0) {
                    additionalTasks.forEach((task, i) => {
                        const taskIndex = taskList.findIndex(
                            (item) => item._id === task._id
                        );
                        taskList.splice(taskIndex, 1);
                        moveTaskToDestination(
                            destinationTaskList,
                            task,
                            destination.index + i + 1
                        );
                    });

                    setEditingTasks((prev) => [
                        ...prev.filter(
                            (item) =>
                                !additionalTasks.some(
                                    (task) => task._id === item._id
                                )
                        ),
                        ...additionalTasks,
                    ]);
                }
            }

            movedTask.assigneeId =
                destinationId.split('-')[1] || UNASSIGNED_TASKS_ID;

            if (sourceId !== UNASSIGNED_TASKS_POOL_ID) {
                updateHousekeeperTasks(sourceId, taskList);
            }
            if (destinationId !== UNASSIGNED_TASKS_POOL_ID) {
                updateHousekeeperTasks(destinationId, destinationTaskList);
            } else {
                setEditingTasks((prev) => {
                    const prevTasks = [...prev].filter(
                        (item) => movedTask._id !== item._id
                    );

                    return [...prevTasks, movedTask];
                });

                newUnassignedTasks = destinationTaskList;
            }
        }

        setHousekeepersByTab(newHousekeepers);
        setUnassignedTasks(filterTasksByTab(newUnassignedTasks, selectedTab));
    };

    const logOut = async () => {
        try {
            removeStorageItem('userEmail');
            dispatch({
                type: 'ADD_USER',
                email: '',
            });
            await signOut(auth);
        } catch (err) {
            console.error(err);
        }
    };

    const saveChanges = async () => {
        setIsLoading(true);

        try {
            const { failedTasks } = await editTasks(editedTasks);

            if (failedTasks.length > 0) {
                failedTasks.forEach(({ task, error }) => {
                    notification.error({
                        message: error || `Failed to move task`,
                        style: {
                            width: 300,
                        },
                        description: (
                            <div>
                                <p
                                    style={{
                                        fontWeight: 'bold',
                                        fontSize: '18px',
                                    }}
                                >
                                    {task.listing.nickname}
                                </p>
                                <p style={{ fontWeight: 'bold' }}>
                                    {task.duration} Min
                                </p>
                                <p>{task.title}</p>
                            </div>
                        ),
                        showProgress: true,
                    });
                });

                await fetchDataAndSet();
            }
        } catch (error) {
            notification.error({
                message: error.response.data.message || error.message,
                showProgress: true,
            });
        } finally {
            setIsDragging(false);
            setEditingTasks([]);
            setIsLoading(false);
        }
    };

    const discardChanges = () => {
        setIsDragging(false);
        setEditingTasks([]);
        housekeepersByTasks();
    };

    const copyTodaysReport = () => {
        const report = generateReport(housekeepersByTab);
        navigator.clipboard
            .writeText(report)
            .then(() =>
                notification.success({
                    message: 'The report was copied.',
                    showProgress: true,
                })
            )
            .catch(() =>
                notification.error({
                    message: 'Failed to copy the report.',
                    showProgress: true,
                })
            );
    };

    // Expose a simple agent API for automation/testing tools.
    // Provides: getTasks(), moveTask(taskId, toHousekeeperId, order?), reorderTasks(housekeeperId, orderedTaskIds)
    // save() is intentionally disabled below - see note near the bottom of this effect.
    useEffect(() => {
        const matchesId = (task, id) =>
            String(task._id) === String(id) || String(task.id) === String(id);

        const enrichTask = (task, index, bundleDurationByKey) => {
            const bundleKey = task.hotelApartment || task.listing?.nickname || '';
            const hotelCode = bundleKey.split(' ')[0] || '';
            const durationMinutes = Number(task.duration) || 0;
            const bundleDuration =
                bundleDurationByKey.get(bundleKey) ?? durationMinutes;

            const deadline = dayjs(task.deadline);
            const start = dayjs(task.start);
            const slackMinutes =
                deadline.isValid() && start.isValid()
                    ? deadline.diff(start, 'minute') - bundleDuration
                    : null;

            const scheduleDate = date && date.format ? date : dayjs(date);

            return {
                ...task,
                sortIndex: index,
                hotelCode,
                bundleKey,
                durationMinutes,
                slackMinutes,
                sameDayCheckIn: deadline.isValid()
                    ? deadline.isSame(scheduleDate, 'day')
                    : false,
                deadlineImpossible: slackMinutes !== null && slackMinutes < 0,
                additionalType: getAdditionalType(task.title),
            };
        };

        const getTasks = () => {
            const allTasksFlat = [
                ...(housekeepersByTab || []).flatMap((h) => h.tasks || []),
                ...(unassignedTasks || []),
            ];

            const bundleDurationByKey = new Map();
            allTasksFlat.forEach((t) => {
                const key = t.hotelApartment || t.listing?.nickname || '';
                const duration = Number(t.duration) || 0;
                bundleDurationByKey.set(
                    key,
                    (bundleDurationByKey.get(key) || 0) + duration
                );
            });

            const housekeepers = (housekeepersByTab || []).map((h) => ({
                id: h.id,
                name: h.name || h.employee_name || h.employee_name,
                mail: h.mail,
                tasks: (h.tasks || []).map((t, index) =>
                    enrichTask(t, index, bundleDurationByKey)
                ),
                tasksCount: h.tasksCount || (h.tasks || []).length,
            }));

            return {
                housekeepers,
                unassignedTasks: (unassignedTasks || []).map((t, index) =>
                    enrichTask(t, index, bundleDurationByKey)
                ),
                editedTasks: (editedTasks || []).map((t) => ({ ...t })),
                selectedTab,
                date: date && date.format ? date.format('YYYY-MM-DD') : date,
            };
        };

        const moveTask = (taskId, toHousekeeperId, order) => {
            if (!taskId) return { success: false, error: 'no taskId' };

            // deep copy current lists
            let newHousekeepers = JSON.parse(JSON.stringify(housekeepersByTab || []));
            let newUnassigned = JSON.parse(JSON.stringify(unassignedTasks || []));

            // find and remove the task from anywhere
            let movedTask = null;

            for (const h of newHousekeepers) {
                const idx = (h.tasks || []).findIndex((t) => matchesId(t, taskId));
                if (idx > -1) {
                    movedTask = h.tasks.splice(idx, 1)[0];
                    break;
                }
            }

            if (!movedTask) {
                const idx = newUnassigned.findIndex((t) => matchesId(t, taskId));
                if (idx > -1) movedTask = newUnassigned.splice(idx, 1)[0];
            }

            if (!movedTask) return { success: false, error: 'task not found' };

            const insertAt = (list, item) => {
                if (typeof order === 'number') {
                    const index = Math.max(0, Math.min(order, list.length));
                    list.splice(index, 0, item);
                } else {
                    list.push(item);
                }
            };

            // update assigneeId
            if (!toHousekeeperId || toHousekeeperId === UNASSIGNED_TASKS_ID) {
                movedTask.assigneeId = UNASSIGNED_TASKS_ID;
                insertAt(newUnassigned, movedTask);
            } else {
                movedTask.assigneeId = toHousekeeperId;
                const hk = newHousekeepers.find((h) => String(h.id) === String(toHousekeeperId));
                if (hk) {
                    hk.tasks = hk.tasks || [];
                    insertAt(hk.tasks, movedTask);
                    hk.tasksCount = hk.tasks.length;
                } else {
                    // if destination housekeeper not found, append a new one
                    newHousekeepers.push({
                        id: toHousekeeperId,
                        name: String(toHousekeeperId),
                        mail: '',
                        tasks: [movedTask],
                        tasksCount: 1,
                    });
                }
            }

            // update editingTasks to include movedTask so saveChanges will persist it
            const newEdited = [...(editedTasks || []).filter((t) => t._id !== movedTask._id), movedTask];

            // commit to React state
            setHousekeepersByTab(newHousekeepers);
            setUnassignedTasks(filterTasksByTab(newUnassigned, selectedTab));
            setEditingTasks(newEdited);
            setIsDragging(true);

            return { success: true, task: movedTask };
        };

        const reorderTasks = (housekeeperId, orderedTaskIds) => {
            if (!Array.isArray(orderedTaskIds)) {
                return { success: false, error: 'orderedTaskIds must be an array' };
            }

            const newHousekeepers = JSON.parse(JSON.stringify(housekeepersByTab || []));
            let newUnassigned = JSON.parse(JSON.stringify(unassignedTasks || []));

            const isUnassigned = !housekeeperId || housekeeperId === UNASSIGNED_TASKS_ID;
            const hk = isUnassigned
                ? null
                : newHousekeepers.find((h) => String(h.id) === String(housekeeperId));

            if (!isUnassigned && !hk) {
                return { success: false, error: 'housekeeper not found' };
            }

            const list = isUnassigned ? newUnassigned : hk.tasks || [];

            const mentioned = new Set(orderedTaskIds.map(String));
            const reordered = orderedTaskIds
                .map((id) => list.find((t) => matchesId(t, id)))
                .filter(Boolean);
            const remaining = list.filter((t) => !mentioned.has(String(t._id)) && !mentioned.has(String(t.id)));
            const finalList = [...reordered, ...remaining];

            if (isUnassigned) {
                newUnassigned = finalList;
            } else {
                hk.tasks = finalList;
                hk.tasksCount = finalList.length;
            }

            const reorderedIds = new Set(finalList.map((t) => String(t._id || t.id)));
            const newEdited = [
                ...(editedTasks || []).filter((t) => !reorderedIds.has(String(t._id || t.id))),
                ...finalList,
            ];

            setHousekeepersByTab(newHousekeepers);
            setUnassignedTasks(filterTasksByTab(newUnassigned, selectedTab));
            setEditingTasks(newEdited);
            setIsDragging(true);

            return { success: true, order: finalList.map((t) => t._id || t.id) };
        };

        // save() is disabled for now - persisting via the agent is considered
        // too risky until this API has been validated further. Kept here,
        // commented out, so it can be re-enabled by uncommenting below.
        // const save = async () => {
        //     try {
        //         await saveChanges();
        //         return { success: true };
        //     } catch (err) {
        //         return { success: false, error: err && err.message ? err.message : err };
        //     }
        // };

        window.agentAPI = {
            getTasks,
            moveTask,
            reorderTasks,
        };

        return () => {
            try {
                if (window.agentAPI) delete window.agentAPI;
            } catch (e) {
                // ignore
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [housekeepersByTab, unassignedTasks, editedTasks, selectedTab, date]);

    return (
        <Layout>
            <Header
                style={{
                    display: 'flex',
                    gap: 16,
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    backgroundColor: '#ffcd9e',
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        gap: 16,
                        alignItems: 'center',
                    }}
                >
                    <Button
                        type='primary'
                        onClick={handleAddNewTaskClick}
                        loading={isLoading}
                    >
                        Add New Task
                    </Button>
                    <Button
                        type='primary'
                        onClick={handleAddEmployeeClick}
                        loading={isLoading}
                    >
                        Add New Employee
                    </Button>
                    <DatePicker
                        onChange={onDateChange}
                        defaultValue={date}
                        value={date}
                        format={'YYYY-MM-DD'}
                        disabledDate={(current) => {
                            return current && current <= dayjs();
                        }}
                        disabled={isLoading}
                    />
                    <Button
                        onClick={refreshData}
                        loading={isLoading}
                        type='primary'
                    >
                        Refresh
                    </Button>
                </div>
                <div
                    style={{
                        display: 'flex',
                        gap: 16,
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <p style={{ marginBottom: 0, fontWeight: 'bold' }}>
                        {getStorageItem('userEmail')}
                    </p>
                    <Button onClick={logOut} loading={isLoading} type='text'>
                        Log out
                    </Button>
                </div>
            </Header>
            <Content style={{ padding: '20px' }}>
                <Spin spinning={isLoading} tip='Loading...'>
                    {isDragging ? (
                        <div
                            style={{
                                position: 'absolute',
                                right: 0,
                                zIndex: 1,
                                display: 'flex',
                                gap: 16,
                            }}
                        >
                            <Button
                                onClick={saveChanges}
                                loading={isLoading}
                                type='primary'
                            >
                                Save
                            </Button>
                            <Button
                                onClick={discardChanges}
                                loading={isLoading}
                                type='primary'
                            >
                                Discard changes
                            </Button>
                        </div>
                    ) : housekeepersByTab.filter((h) => h.tasks.length)
                          .length ? (
                        <div
                            style={{
                                position: 'absolute',
                                right: 0,
                                zIndex: 1,
                                display: 'flex',
                                gap: 16,
                            }}
                        >
                            <Button onClick={copyTodaysReport} type='primary'>
                                Copy Todays Report
                            </Button>
                        </div>
                    ) : null}

                    <Tabs
                        activeKey={selectedTab}
                        onChange={onTabChange}
                        type='card'
                        centered
                        items={Object.keys(TABS).map((v) => {
                            return {
                                label: v,
                                key: v,
                                children: (
                                    <DragDropContext onDragEnd={onDragEnd}>
                                        <div
                                            style={{
                                                overflow: 'auto',
                                                maxHeight:
                                                    'calc(100vh - 160px)',
                                            }}
                                        >
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    gap: 20,
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        minWidth: 260,
                                                        width: 260,
                                                    }}
                                                >
                                                    <UnassignedTasksPool
                                                        handleEditTask={
                                                            handleEditTask
                                                        }
                                                        tasks={unassignedTasks}
                                                    />
                                                </div>
                                                {housekeepersByTab.map(
                                                    (housekeeper) => {
                                                        return (
                                                            <div
                                                                key={
                                                                    housekeeper.id
                                                                }
                                                                style={{
                                                                    minWidth: 260,
                                                                    width: 260,
                                                                }}
                                                            >
                                                                <HousekeeperColumn
                                                                    handleEditTask={
                                                                        handleEditTask
                                                                    }
                                                                    housekeeper={{
                                                                        ...housekeeper,
                                                                        tasks: filterTasksByTab(
                                                                            housekeeper.tasks,
                                                                            selectedTab
                                                                        ),
                                                                    }}
                                                                />
                                                            </div>
                                                        );
                                                    }
                                                )}
                                            </div>
                                        </div>
                                    </DragDropContext>
                                ),
                            };
                        })}
                    />
                </Spin>
            </Content>
            {isAddTaskModalVisible && (
                <AddTaskModal
                    editingTask={editingTask}
                    handleFormSubmit={handleFormSubmit}
                    handleModalClose={handleTaskModalClose}
                    isModalVisible={isAddTaskModalVisible}
                    date={date}
                    users={guestyEmployees}
                />
            )}
            {isAddEmployeeModalVisible && (
                <AddEmployeeModal
                    handleFormSubmit={handleNewEmployeeSubmit}
                    handleModalClose={handleEmployeeModalClose}
                    isModalVisible={isAddEmployeeModalVisible}
                    housekeepersByTab={housekeepersByTab}
                    users={guestyEmployees}
                />
            )}
        </Layout>
    );
}

export default Dashboard;
